import React from "react";
import YouTube from "react-youtube";
import {
  Play, Pause, SkipForward, SkipBack, Stop, Shuffle, SpeakerHigh, SpeakerLow, CaretUp, CaretDown, MusicNotes, DotsSixVertical,
} from "@phosphor-icons/react";
import { useBgMusic } from "../context/BackgroundMusicContext";
import useDraggableFAB from "../hooks/useDraggableFAB";

export default function BackgroundMusicPlayer() {
  const bg = useBgMusic();
  const { queue = [] } = bg || {};
  // Drag wiring — applied to the outer wrapper so the floating
  // Background-music widget can be tucked anywhere on the screen
  // (and persists across page navigations). Storage key is
  // separate from the MiniPlayer's so they remember independent
  // positions.
  const fab = useDraggableFAB({
    storageKey: "hw:fab:bgmusic",
    defaultPosition: { right: 16, bottom: 96 },
    size: 220,
  });
  if (!bg) return null;
  const { current, isPlaying, shuffle, volume, minimized,
    setShuffle, setVolume, setMinimized,
    togglePlay, next, prev, stop,
    onPlayerReady, onPlayerStateChange, onPlayerError,
  } = bg;

  if (queue.length === 0) return null;

  // Feed YouTube a NATIVE playlist (videoId + `playlist` playerVar) so
  // the player advances track-to-track by itself. This is the key to
  // mobile auto-advance: loading the next video manually gets blocked
  // on phones, but the player continuing its own playlist stays within
  // the user's original tap. `key` is the id list, so the iframe is
  // built once per queue and NOT remounted when the track changes.
  const videoIds = queue.map((q) => q.youtube_id).filter(Boolean);
  const playlistKey = videoIds.join(",");

  return (
    <>
      {/* Hidden YouTube player — single persistent instance running a
          native playlist. YouTube auto-skips unplayable videos and
          auto-advances on its own (works on mobile). */}
      <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", opacity: 0, pointerEvents: "none" }} aria-hidden="true">
        {videoIds.length > 0 && (
          <YouTube
            key={playlistKey}
            videoId={videoIds[0]}
            opts={{
              width: "1", height: "1",
              host: "https://www.youtube-nocookie.com",
              playerVars: {
                autoplay: 1, controls: 0, modestbranding: 1, rel: 0,
                iv_load_policy: 3, playsinline: 1,
                playlist: videoIds.slice(1).join(","),
                loop: 1,
              },
            }}
            onReady={(e) => onPlayerReady(e.target)}
            onStateChange={(e) => onPlayerStateChange(e.data)}
            onError={(e) => onPlayerError && onPlayerError(e.data)}
          />
        )}
      </div>

      {/* Floating draggable mini-player. Shrunk down and re-styled so
          it tucks neatly into a phone corner; the drag handle is the
          forest-green header row. The user can carry it to any page. */}
      <div
        ref={fab.ref}
        style={fab.style}
        className={`z-[60] transition-all ${fab.dragging ? "ring-4 ring-terracotta/50 scale-[1.02]" : ""} ${minimized ? "w-12 h-12" : "w-[220px] md:w-[240px]"}`}
        data-testid="bg-music-player"
      >
        {minimized ? (
          <button
            onClick={() => setMinimized(false)}
            data-testid="bg-music-expand"
            data-no-drag="1"
            className="w-12 h-12 text-forest hover:text-terracotta grid place-items-center shadow-xl hover:scale-105 transition-all relative"
            title={current ? `${current.title} — ${current.artist}` : "Music"}
          >
            <MusicNotes size={18} weight="fill" />
            {isPlaying && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-terracotta animate-pulse" />}
          </button>
        ) : (
          <div className="glass rounded-2xl border border-cream-300/60 shadow-[0_-8px_32px_rgba(0,0,0,0.10)] overflow-hidden">
            <div className="px-2.5 py-2 flex items-center gap-1.5 border-b border-cream-300/60 bg-forest text-cream cursor-grab active:cursor-grabbing">
              <DotsSixVertical size={12} weight="bold" className="opacity-70 shrink-0" />
              <MusicNotes size={12} weight="fill" className="text-terracotta shrink-0" />
              <span className="text-[9px] tracking-[0.2em] uppercase font-semibold flex-1 truncate">Gospel mix</span>
              <button onClick={() => setMinimized(true)} data-testid="bg-music-minimize" data-no-drag="1" className="opacity-70 hover:opacity-100" title="Minimize">
                <CaretDown size={12} weight="bold" />
              </button>
              <button onClick={stop} data-testid="bg-music-close" data-no-drag="1" className="opacity-70 hover:opacity-100 ml-0.5" title="Stop">
                <Stop size={12} weight="fill" />
              </button>
            </div>
            <div className="px-2.5 py-2">
              <p className="font-heading text-sm text-forest truncate" data-testid="bg-music-title">{current?.title || "—"}</p>
              <p className="text-[10px] text-muted_ink truncate">{current?.artist || ""}</p>

              <div className="mt-2 flex items-center gap-1" data-no-drag="1">
                <button onClick={() => setShuffle(!shuffle)} data-testid="bg-music-shuffle" data-no-drag="1" className={`w-8 h-8 rounded-full grid place-items-center transition-all ${shuffle ? "bg-terracotta text-cream" : "bg-cream-200 hover:bg-cream-300"}`} title="Shuffle">
                  <Shuffle size={12} weight="bold" />
                </button>
                <button onClick={prev} data-testid="bg-music-prev" data-no-drag="1" className="w-8 h-8 text-ink/70 hover:text-ink grid place-items-center transition-all" title="Previous">
                  <SkipBack size={12} weight="fill" />
                </button>
                <button onClick={togglePlay} data-testid="bg-music-toggle" data-no-drag="1" className="w-10 h-10 text-forest hover:text-terracotta grid place-items-center transition-all hover:scale-105 mx-0.5" title={isPlaying ? "Pause" : "Play"}>
                  {isPlaying ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
                </button>
                <button onClick={next} data-testid="bg-music-next" data-no-drag="1" className="w-8 h-8 text-ink/70 hover:text-ink grid place-items-center transition-all" title="Next">
                  <SkipForward size={12} weight="fill" />
                </button>
                <div className="flex-1" />
                {volume < 30 ? <SpeakerLow size={12} className="text-muted_ink shrink-0" /> : <SpeakerHigh size={12} className="text-muted_ink shrink-0" />}
              </div>

              <input
                type="range"
                min={0} max={100} step={5}
                value={volume}
                onChange={(e) => setVolume(parseInt(e.target.value, 10))}
                data-testid="bg-music-volume"
                data-no-drag="1"
                className="w-full mt-1.5 accent-terracotta"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
